import React from 'react';
import { cx } from './classNames';
import { Spinner } from './Spinner';
import './ui.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'title'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  /** 通信中。スピナーを出し、押せなくする */
  loading?: boolean;
  /**
   * 押せない理由（原則 8）。指定すると disabled になり、title として表示される。
   * 「なぜ押せないか」を必ず添えるための入口なので、title は別に受け付けない。
   */
  disabledReason?: string;
  /** 親の幅いっぱいに広げる（スマホのフォーム末尾など） */
  block?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    iconLeft,
    iconRight,
    loading = false,
    disabledReason,
    block = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = Boolean(disabled || loading || disabledReason);

  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'ui-btn',
        `ui-btn--${variant}`,
        `ui-btn--${size}`,
        loading && 'ui-btn--loading',
        block && 'ui-btn--block',
        className,
      )}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      title={disabledReason}
      {...rest}
    >
      {loading ? (
        <span className="ui-btn__icon">
          <Spinner size="sm" />
        </span>
      ) : (
        iconLeft && <span className="ui-btn__icon">{iconLeft}</span>
      )}
      {children != null && <span className="ui-btn__label">{children}</span>}
      {iconRight && !loading && <span className="ui-btn__icon">{iconRight}</span>}
    </button>
  );
});
